import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Chip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import EastIcon from "@mui/icons-material/East";
import { put, api, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const SectionHeading = ({ text, sx }) => (
  <p
    className={styles.ques_heading}
    style={{
      marginTop: "1rem",
      marginLeft: "0",
      textIndent: "-1rem",
      paddingLeft: "1rem",
      lineHeight: "1.5",
      ...sx,
    }}
  >
    {text}
  </p>
);

const CustomTextField = ({
  value,
  onChange,
  placeholder,
  error,
  helperText,
  disabled,
}) => (
  <Box sx={{ marginLeft: "1rem" }}>
    <TextField
      fullWidth
      multiline
      // rows={1}
      placeholder={placeholder || "Enter Text"}
      value={value}
      disabled={disabled}
      onChange={onChange}
      sx={{
        "& .MuiOutlinedInput-root": { padding: "0.6rem" },
        marginTop: "-0.5rem",
      }}
      error={error}
      helperText={
        <Typography
          variant="body2"
          color="error"
          style={{
            lineHeight: "1.2",
            margin: "2px 0",
            color: "#d34040",
            fontSize: "10px",
            fontStyle: "italic",
            marginLeft: "-10px",
          }}
        >
          {helperText}
        </Typography>
      }
    />
  </Box>
);

const GeneralDisclosure26 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [errors, setErrors] = useState({});
  const [disabled, setDisabled] = useState(false);
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [griReview, setGriReview] = useState(() =>
    JSON.parse(localStorage.getItem("griReview"))
  );

  const [formData, setFormData] = useState({
    operatingSectors: [],
    valueChainEntitiesActivities: {
      coreActivitiesProductsServicesMarkets: "",
      supplyChainDetails: "",
      downstreamEntitiesActivities: "",
    },
    businessRelationshipsSustainabilityPerformance: "",
    industrySectorChangeBusinessRelationships: "",
  });
  const [sectorInput, setSectorInput] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(
          `/gri/get?Id=${id}&type=GeneralDisclosures2021`
        );
        const data =
          response.data.data.GeneralDisclosures2021
            ?.activitiesValueChainBusinessRelationships || {};
        if (response?.data?.data?.year < currentYear - 1) setDisabled(true);
        setFormData({
          // operatingSectors: data.operatingSectors || [],
          operatingSectors:
            data.operatingSectors?.map((sector) => ({ name: sector })) || [],
          valueChainEntitiesActivities: {
            coreActivitiesProductsServicesMarkets:
              data.valueChainEntitiesActivities
                ?.coreActivitiesProductsServicesMarkets || "",
            supplyChainDetails:
              data.valueChainEntitiesActivities?.supplyChainDetails || "",
            downstreamEntitiesActivities:
              data.valueChainEntitiesActivities?.downstreamEntitiesActivities ||
              "",
          },
          businessRelationshipsSustainabilityPerformance:
            data.businessRelationshipsSustainabilityPerformance || "",
          industrySectorChangeBusinessRelationships:
            data.industrySectorChangeBusinessRelationships || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleAddSector = () => {
    if (sectorInput.trim()) {
      const newSector = {
        id: Date.now(),
        name: sectorInput.trim(),
      };
      setFormData((prev) => ({
        ...prev,
        operatingSectors: [...prev.operatingSectors, newSector],
      }));
      setSectorInput("");
      setErrors((prev) => ({ ...prev, sector: "" })); // Clear error after adding
    } else {
      setErrors((prev) => ({ ...prev, sector: "This field is required" }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => ({ ...prev, [field]: "" })); // Clear error if input is valid
  };

  const handleNestedInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      valueChainEntitiesActivities: {
        ...prev.valueChainEntitiesActivities,
        [field]: value,
      },
    }));

    // Check if the value is valid (non-empty) and remove the error
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDeleteSector = (id) => {
    setFormData((prev) => ({
      ...prev,
      operatingSectors: prev.operatingSectors.filter(
        (sector) => sector.id !== id
      ),
    }));
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector
    // if (sectorInput.trim() === "" && formData.operatingSectors.length === 0) {
    //   newErrors.sector = "This field is required";
    //   formIsValid = false;
    // }
    if (sectorInput.trim() !== "") {
      newErrors.sector = "You have entered a sector but haven't added it yet.";
      formIsValid = false;
    } else if (formData.operatingSectors.length === 0) {
      newErrors.sector = "This field is required";
      formIsValid = false;
    }

    // Validate required fields in the formData
    if (
      !formData.valueChainEntitiesActivities
        .coreActivitiesProductsServicesMarkets
    ) {
      newErrors.coreActivitiesProductsServicesMarkets =
        "This field is required";
      formIsValid = false;
    }

    if (!formData.valueChainEntitiesActivities.supplyChainDetails) {
      newErrors.supplyChainDetails = "This field is required";
      formIsValid = false;
    }

    if (!formData.valueChainEntitiesActivities.downstreamEntitiesActivities) {
      newErrors.downstreamEntitiesActivities = "This field is required";
      formIsValid = false;
    }

    if (!formData.businessRelationshipsSustainabilityPerformance) {
      newErrors.businessRelationshipsSustainabilityPerformance =
        "This field is required";
      formIsValid = false;
    }

    if (!formData.industrySectorChangeBusinessRelationships) {
      newErrors.industrySectorChangeBusinessRelationships =
        "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) return; // Validate form before submission

      console.log("Form Data Submitted:", formData);

      // Convert the operatingSectors to a comma-separated string
      // const operatingSectorsString = formData.operatingSectors
      //   .map((sector) => sector.name)
      //   .join(",");

      const operatingSectorsString = formData.operatingSectors
        .map((sector) => sector.name)

      const payload = {
        type: "GeneralDisclosures2021",
        submitted2_6: true,
        activitiesValueChainBusinessRelationships: {
          ...formData,
          operatingSectors: operatingSectorsString, // Store the comma-separated string here
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/general-disclosure/2-7/${id}`);
      } catch (error) {
        console.error("Error updating data:", error);
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/general-disclosure/2-7/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {/* <SectionHeading text="2-1 Activities, value chain and other business relationships" /> */}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0rem" }}
      >
        2-6 Activities, value chain and other business relationships
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}
        <SectionHeading text="a. Report the sectors in which organization is operating or active." />
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <Box sx={{ width: "58%" }}>
            <CustomTextField
              value={sectorInput}
              disabled={disabled}
              onChange={(e) => setSectorInput(e.target.value)}
              error={!!errors.sector}
              // helperText={errors.sector}
            />
          </Box>
          {!hide && (
            <Button
              startIcon={<Add />}
              disabled={disabled}
              onClick={handleAddSector}
              variant="outlined"
              sx={{
                height: "2.6rem",
                marginTop: "-0.8rem",
                color: "#1C486B",
                width: "15%",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Add Sector
            </Button>
          )}
        </Box>
        <Typography
          variant="body2"
          color="error"
          style={{
            lineHeight: "1.2",
            // margin: "2px 0",
            marginLeft: "1rem",
            color: "#d34040",
            fontSize: "10px",
            fontStyle: "italic",
          }}
        >
          {errors.sector}
        </Typography>

        {/* Displaying Added Sectors as Chips */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            marginTop: "1rem",
            marginLeft: "1rem",
          }}
        >
          {formData.operatingSectors.map((sector) => (
            <Chip
              key={sector.id}
              label={sector.name}
              onDelete={() => handleDeleteSector(sector.id)}
              sx={{
                backgroundColor: "#1C486B",
                color: "#f0f0f0",
                fontWeight: "bold",
                "& .MuiChip-deleteIcon": {
                  color: "#ededed",
                  borderRadius: "50%",
                },
              }}
            />
          ))}
        </Box>

        {/* Section B */}
        <SectionHeading text="b. Describe organization's value chain including entities upstreams and downstreams activities." />
        <Box sx={{ marginLeft: "1.2rem" }}>
          <SectionHeading
            text="i. Describe the organization’s core activities, the products or services it offer, and the markets it serve."
            sx={{ marginTop: "-0.5rem" }}
          />
          <CustomTextField
            value={
              formData.valueChainEntitiesActivities
                .coreActivitiesProductsServicesMarkets
            }
            onChange={(e) =>
              handleNestedInputChange(
                "coreActivitiesProductsServicesMarkets",
                e.target.value
              )
            }
            disabled={disabled}
            error={errors.coreActivitiesProductsServicesMarkets}
            helperText={errors.coreActivitiesProductsServicesMarkets}
          />

          <SectionHeading text="ii. Describe the organization’s supply chain consider providing details on supplier types, sector characteristics, monetary value, geographic location, suppliers activities, business relationships etc." />
          <CustomTextField
            value={formData.valueChainEntitiesActivities.supplyChainDetails}
            onChange={(e) =>
              handleNestedInputChange("supplyChainDetails", e.target.value)
            }
            error={errors.supplyChainDetails}
            helperText={errors.supplyChainDetails}
            disabled={disabled}
          />

          <SectionHeading text="iii. Describe the entities downstream from organization and it's activities related to organization's offerings." />
          <CustomTextField
            value={
              formData.valueChainEntitiesActivities.downstreamEntitiesActivities
            }
            onChange={(e) =>
              handleNestedInputChange(
                "downstreamEntitiesActivities",
                e.target.value
              )
            }
            error={errors.downstreamEntitiesActivities}
            helperText={errors.downstreamEntitiesActivities}
            disabled={disabled}
          />
        </Box>

        {/* Section C */}
        <SectionHeading text="c. Describe organization's other significant business relationships beyond its immediate suppliers and customers that are relevant to organization's sustainability performance." />
        <CustomTextField
          value={formData.businessRelationshipsSustainabilityPerformance}
          onChange={(e) =>
            handleInputChange(
              "businessRelationshipsSustainabilityPerformance",
              e.target.value
            )
          }
          error={errors.businessRelationshipsSustainabilityPerformance}
          helperText={errors.businessRelationshipsSustainabilityPerformance}
          disabled={disabled}
        />

        {/* Section D */}
        <SectionHeading text="d. Describe any significant changes in organization's industry sector(s), value chain, or other relevant business relationships compared to the previous reporting period." />
        <CustomTextField
          value={formData.industrySectorChangeBusinessRelationships}
          onChange={(e) =>
            handleInputChange(
              "industrySectorChangeBusinessRelationships",
              e.target.value
            )
          }
          error={errors.industrySectorChangeBusinessRelationships}
          helperText={errors.industrySectorChangeBusinessRelationships}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GeneralDisclosure26;
